/**
 * Resolves Firebase Storage URLs to signed download URLs for iOS compatibility
 * Uses getDownloadURL() to get properly signed URLs that work with iOS WebView
 */

import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { Capacitor } from '@capacitor/core';

// In-memory cache to avoid repeated Firebase Storage API calls
const urlCache = new Map();

/**
 * Resolves a Firebase Storage URL to a signed download URL
 * Only runs on native platforms (iOS/Android) where WebView needs signed URLs
 * 
 * @param {string} url - The image URL (may be Firebase Storage or external)
 * @returns {Promise<string>} - The resolved download URL
 */
export async function resolveFirebaseImageUrl(url) {
  // Early return for non-native platforms (web works fine without this)
  if (!Capacitor.isNativePlatform()) {
    return url;
  }

  // Early return for non-Firebase Storage URLs
  if (!url || typeof url !== 'string') {
    return url;
  }

  // Only process Firebase Storage URLs
  const isFirebaseStorage = 
    url.includes('firebasestorage.googleapis.com') || 
    url.includes('storage.googleapis.com') ||
    url.startsWith('gs://');

  if (!isFirebaseStorage) {
    return url;
  }

  // Check cache first
  if (urlCache.has(url)) {
    console.log('📦 Cache hit for Firebase Storage URL');
    return urlCache.get(url);
  }

  try {
    console.log('🔄 Resolving Firebase Storage URL for native platform...');
    
    // Extract the file path from the URL
    let filePath;
    
    if (url.startsWith('gs://')) {
      // gs://bucket/path/to/file.jpg -> path/to/file.jpg
      filePath = url.replace(/^gs:\/\/[^/]+\//, '');
    } else if (url.includes('firebasestorage.googleapis.com')) {
      // https://firebasestorage.googleapis.com/v0/b/bucket/o/path%2Fto%2Ffile.jpg?...
      const match = url.match(/\/o\/([^?]+)/);
      if (match) {
        filePath = decodeURIComponent(match[1]);
      }
    } else if (url.includes('storage.googleapis.com')) {
      // https://storage.googleapis.com/bucket/path/to/file.jpg
      const match = url.match(/\.googleapis\.com\/[^/]+\/(.+)/);
      if (match) {
        filePath = match[1];
      }
    }

    if (!filePath) {
      console.warn('⚠️ Could not extract file path from URL:', url);
      return url;
    }

    // Get signed download URL using Firebase Storage SDK
    const storage = getStorage();
    const fileRef = ref(storage, filePath);
    const downloadURL = await getDownloadURL(fileRef);
    
    console.log('✅ Resolved Firebase Storage URL successfully');
    
    // Cache the result
    urlCache.set(url, downloadURL);
    
    return downloadURL;
    
  } catch (error) {
    console.error('❌ Failed to resolve Firebase Storage URL:', error);
    // Return original URL as fallback
    return url;
  }
}
